import { useEffect, useMemo, useState } from "react";
import { useQueries } from "@tanstack/react-query";
import { adapters } from "./adapterRegistry";
import { MODULE_ORDER, type ModuleSearchResult } from "./types";

const DEBOUNCE_MS = 250;
const MIN_LENGTH = 2;

export interface UseGlobalSearchResult {
  debouncedQ: string;
  modules: ModuleSearchResult[];
  isAnyLoading: boolean;
}

export function useGlobalSearch(q: string): UseGlobalSearchResult {
  const [debouncedQ, setDebouncedQ] = useState("");

  useEffect(() => {
    const handle = setTimeout(() => setDebouncedQ(q), DEBOUNCE_MS);
    return () => clearTimeout(handle);
  }, [q]);

  const enabled = debouncedQ.length >= MIN_LENGTH;

  const queries = useQueries({
    queries: adapters.map((adapter) => ({
      queryKey: ["global-search", adapter.moduleId, debouncedQ] as const,
      queryFn: ({ signal }: { signal: AbortSignal }) =>
        adapter.search(debouncedQ, signal),
      enabled,
      staleTime: 30_000,
      gcTime: 5 * 60_000,
      retry: false,
    })),
  });

  const modules = useMemo<ModuleSearchResult[]>(() => {
    if (!enabled) return [];

    const byModuleId = new Map<string, ModuleSearchResult>();

    adapters.forEach((adapter, idx) => {
      const q = queries[idx];
      const state: ModuleSearchResult["state"] = q.isError
        ? "error"
        : q.isLoading || q.isFetching
          ? "loading"
          : "ok";

      byModuleId.set(adapter.moduleId, {
        moduleId: adapter.moduleId,
        label: adapter.label,
        state,
        results: q.data ?? [],
        error:
          q.error instanceof Error
            ? { message: q.error.message || "Search failed" }
            : q.isError
              ? { message: "Search failed" }
              : undefined,
        refetch: () => {
          void q.refetch();
        },
      });
    });

    return MODULE_ORDER.flatMap((id) => {
      const m = byModuleId.get(id);
      return m ? [m] : [];
    });
  }, [enabled, queries]);

  const isAnyLoading = modules.some((m) => m.state === "loading");

  return { debouncedQ, modules, isAnyLoading };
}
